import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DollarSign, Users, TrendingUp, Layers, Cpu, ArrowRight } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { api } from "@/lib/api";
import { fmtUSDExact } from "@/lib/format";

export default function Admin() {
  const [rev, setRev] = useState(null);
  const [fees, setFees] = useState([]);
  const [users, setUsers] = useState([]);
  const [daily, setDaily] = useState([]);

  useEffect(() => {
    api.get("/admin/revenue").then(({ data }) => setRev(data)).catch(() => {});
    api.get("/admin/fees").then(({ data }) => setFees(data)).catch(() => {});
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/admin/revenue/daily?days=30").then(({ data }) => setDaily(data.series || [])).catch(() => {});
  }, []);

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="admin-page">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">// admin · console</div>
          <h1 className="mt-2 text-4xl md:text-5xl font-black tracking-tighter uppercase">Revenue console</h1>
          <div className="mt-2 font-mono text-xs uppercase tracking-widest text-neutral-400" data-testid="admin-lifetime-revenue">
            lifetime real revenue <span className="text-[#00FF66] text-base font-bold">{rev ? fmtUSDExact(rev.revenue) : "—"}</span>
            <span className="text-neutral-600"> · {rev ? fmtUSDExact(rev.simulated_revenue) : "—"} simulated</span>
          </div>
        </div>
        <Link
          to="/app/admin/providers"
          data-testid="admin-to-providers-link"
          className="inline-flex items-center gap-2 border border-neo-border px-4 py-3 font-mono uppercase tracking-widest text-xs hover:bg-white/5 transition-colors"
        >
          <Cpu className="w-3.5 h-3.5" strokeWidth={1.5} />
          Providers
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </Link>
      </header>

      {/* Real vs simulated revenue split */}
      <section
        className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"
        data-testid="admin-revenue-split"
      >
        <div className="border border-[#00FF66]/50 bg-[#00FF66]/5 p-6" data-testid="admin-real-revenue">
          <div className="flex items-center gap-2 text-[#00FF66]">
            <DollarSign className="w-5 h-5" strokeWidth={1.5} />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Real cash revenue</span>
          </div>
          <div className="mt-3 font-mono text-4xl font-bold tracking-tighter text-[#00FF66]">
            {rev ? fmtUSDExact(rev.revenue) : "—"}
          </div>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-400">
            real fees {rev ? fmtUSDExact(rev.fees_real) : "—"} · top-ups {rev ? fmtUSDExact(rev.total_topups) : "—"} · pro {rev ? fmtUSDExact(rev.pro_revenue) : "—"}
          </div>
        </div>
        <div className="border border-amber-500/40 bg-amber-500/5 p-6" data-testid="admin-sim-revenue">
          <div className="flex items-center gap-2 text-amber-400">
            <Layers className="w-5 h-5" strokeWidth={1.5} />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Simulated fees (play money)</span>
          </div>
          <div className="mt-3 font-mono text-4xl font-bold tracking-tighter text-amber-400">
            {rev ? fmtUSDExact(rev.fees_simulated) : "—"}
          </div>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-400">
            from users on welcome-bonus play money · becomes real once they fund a wallet
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-testid="admin-revenue-widget">
        <BigStat
          icon={<DollarSign className="w-5 h-5" strokeWidth={1.5} />}
          label="Total trade fees"
          value={rev ? fmtUSDExact(rev.total_fees) : "—"}
          note={rev ? `real ${fmtUSDExact(rev.fees_real)} · sim ${fmtUSDExact(rev.fees_simulated)}` : "—"}
          testid="admin-stat-fees"
          highlight
        />
        <BigStat
          icon={<Layers className="w-5 h-5" strokeWidth={1.5} />}
          label="Pro subscriptions"
          value={rev ? fmtUSDExact(rev.pro_revenue) : "—"}
          note={`${rev?.pro_count ?? 0} pro passes`}
          testid="admin-stat-pro"
        />
        <BigStat
          icon={<Users className="w-5 h-5" strokeWidth={1.5} />}
          label="Users"
          value={rev ? String(rev.user_count) : "—"}
          note={`${rev?.real_funded_user_count ?? 0} funded real money`}
          testid="admin-stat-users"
        />
        <BigStat
          icon={<TrendingUp className="w-5 h-5" strokeWidth={1.5} />}
          label="Trades"
          value={rev ? String(rev.trade_count) : "—"}
          note="lifetime executed"
          testid="admin-stat-trades"
        />
      </section>

      <section
        className="border border-neo-border bg-neo-surface mb-8"
        data-testid="admin-revenue-chart"
      >
        <div className="p-4 border-b border-neo-border font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 flex items-center justify-between">
          <span>// real revenue · last 30 days</span>
          <span className="text-[#00FF66]">
            {daily.length ? fmtUSDExact(daily.reduce((s, d) => s + (d.real_revenue || 0), 0)) : "$0.00"} total
          </span>
        </div>
        <div className="p-4 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daily} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="realGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00FF66" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#00FF66" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#262626" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: "#666", fontSize: 10, fontFamily: "monospace" }}
                tickFormatter={(d) => d.slice(5)}
                stroke="#262626"
                minTickGap={24}
              />
              <YAxis tick={{ fill: "#666", fontSize: 10, fontFamily: "monospace" }} stroke="#262626" />
              <Tooltip
                contentStyle={{
                  background: "#0a0a0a",
                  border: "1px solid #262626",
                  borderRadius: 0,
                  fontFamily: "monospace",
                  fontSize: 12,
                }}
                labelStyle={{ color: "#999" }}
                formatter={(v, n) => [fmtUSDExact(v), n === "real_revenue" ? "Real revenue" : n]}
              />
              <Area
                type="monotone"
                dataKey="real_revenue"
                stroke="#00FF66"
                strokeWidth={2}
                fill="url(#realGrad)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-neo-border bg-neo-surface" data-testid="admin-fees-table">
          <div className="p-4 border-b border-neo-border font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            // fee_ledger · {fees.length}
          </div>
          <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-neo-border font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            <div className="col-span-4">Date</div>
            <div className="col-span-2">Asset</div>
            <div className="col-span-2">Sym</div>
            <div className="col-span-2">Side</div>
            <div className="col-span-2 text-right">Fee</div>
          </div>
          <div className="divide-y divide-neo-border font-mono text-sm max-h-[60vh] overflow-y-auto">
            {fees.length === 0 && <div className="p-8 text-center text-neutral-500 text-xs">No fees yet</div>}
            {fees.map((f) => (
              <div key={f.id} className="grid grid-cols-12 gap-2 px-4 py-3">
                <div className="col-span-4 text-neutral-500 text-xs">
                  {new Date(f.created_at).toLocaleString()}
                </div>
                <div className="col-span-2 uppercase text-neutral-300 text-xs">{f.asset_type}</div>
                <div className="col-span-2 uppercase text-white">{f.symbol}</div>
                <div className={`col-span-2 uppercase ${f.side === "buy" ? "pos" : "neg"}`}>{f.side}</div>
                <div className="col-span-2 text-right text-white">{fmtUSDExact(f.amount)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-neo-border bg-neo-surface" data-testid="admin-users-table">
          <div className="p-4 border-b border-neo-border font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            // users · {users.length}
          </div>
          <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-neo-border font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            <div className="col-span-4">Email</div>
            <div className="col-span-4">Name</div>
            <div className="col-span-2 text-right">Cash</div>
            <div className="col-span-2 text-right">Joined</div>
          </div>
          <div className="divide-y divide-neo-border font-mono text-sm max-h-[60vh] overflow-y-auto">
            {users.map((u) => (
              <div key={u.id} className="grid grid-cols-12 gap-2 px-4 py-3">
                <div className="col-span-4 text-neutral-300 truncate">{u.email}</div>
                <div className="col-span-4 text-neutral-400 truncate">
                  {u.name} {u.is_admin ? <span className="text-white">·admin</span> : ""}
                </div>
                <div className="col-span-2 text-right text-white">{fmtUSDExact(u.cash || 0)}</div>
                <div className="col-span-2 text-right text-neutral-500 text-xs">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function BigStat({ icon, label, value, note, testid, highlight }) {
  return (
    <div
      className={`border p-6 ${highlight ? "border-white bg-white/5" : "border-neo-border bg-neo-surface"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-neutral-400">
        {icon}
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">{label}</span>
      </div>
      <div className="mt-3 font-mono text-3xl font-bold tracking-tighter">{value}</div>
      <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
        {note}
      </div>
    </div>
  );
}
